import { useMemo, useRef, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Info, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { DataGrid, type Column } from '@/components/advanced/DataGrid';
import {
  getCachedTabSecurity,
  unscannedTabSecurity,
  type TabSecurityInfo,
  type TabSecurityStatus,
} from '@/components/tabs/tabSecurity';
import {
  EncryptionBadge,
  SecurityScoreBar,
  SecurityStatusBadge,
  TabSecuritySummaryStrip,
} from '@/components/tabs/TabSecurityCells';
import type { SecurityFinding } from '@/services/tabs';

export interface TabSecurityRow {
  id: string;
  title: string;
  url: string;
  score: number | null;
  threats: string[];
  findings: SecurityFinding[];
  status: TabSecurityStatus;
  lastScanned: string;
  encryption: boolean;
}

export function toSecurityRow(
  tab: { id: string; title: string; url: string },
  security?: TabSecurityInfo,
): TabSecurityRow {
  const s = security ?? getCachedTabSecurity(tab.id, tab.url) ?? unscannedTabSecurity(tab.url);
  return {
    id: tab.id,
    title: tab.title || 'Untitled',
    url: tab.url,
    score: s.score,
    threats: s.threats,
    findings: s.findings,
    status: s.status,
    lastScanned: s.lastScanned,
    encryption: s.encryption,
  };
}

const CHECKBOX_CLASS =
  'w-4 h-4 rounded border-khiimori-border bg-khiimori-bg-secondary text-primary-500 focus:ring-primary-500/50 shrink-0';

function buildSecurityColumns(
  selection?: {
    selectedIds: Set<string>;
    onToggle: (id: string) => void;
  },
  onDelete?: (id: string) => void,
): Column<TabSecurityRow>[] {
  const cols: Column<TabSecurityRow>[] = [];

  if (selection) {
    cols.push({
      key: 'id',
      header: '',
      width: '2.5rem',
      render: (_value, row) => (
        <input
          type="checkbox"
          checked={selection.selectedIds.has(row.id)}
          onChange={(e) => {
            e.stopPropagation();
            selection.onToggle(row.id);
          }}
          onClick={(e) => e.stopPropagation()}
          className={CHECKBOX_CLASS}
          aria-label={`Select ${row.title}`}
        />
      ),
    });
  }

  cols.push(
  {
    key: 'title',
    header: 'Tab',
    sortable: true,
    width: '32%',
    render: (value, row) => (
      <div className="min-w-0">
        <p className="font-medium text-khiimori-text-primary truncate" title={String(value)}>
          {value}
        </p>
        <p className="text-xs text-khiimori-text-secondary truncate" title={row.url}>
          {row.url}
        </p>
      </div>
    ),
  },
  {
    key: 'score',
    header: 'Security Score',
    sortable: true,
    width: '14%',
    render: (value: number | null) => <SecurityScoreBar score={value} compact />,
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    width: '11%',
    render: (value: TabSecurityStatus) => <SecurityStatusBadge status={value} />,
  },
  {
    key: 'encryption',
    header: 'Encryption',
    width: '11%',
    render: (value: boolean) => <EncryptionBadge encryption={value} />,
  },
  {
    key: 'findings',
    header: 'Findings',
    width: '10%',
    render: (value: SecurityFinding[], row) => {
      if (row.status === 'unscanned') {
        return <span className="text-sm text-khiimori-text-muted">—</span>;
      }
      if (value.length === 0) {
        return <span className="text-sm text-khiimori-text-secondary">None</span>;
      }
      const serious = value.some((f) => f.severity !== 'low');
      return (
        <span
          className={`text-sm ${serious ? 'text-red-400' : 'text-khiimori-text-secondary'}`}
          title={value.map((f) => f.description).join('\n')}
        >
          {value.length} found
        </span>
      );
    },
  },
  {
    key: 'lastScanned',
    header: 'Last Scan',
    width: '12%',
    render: (value: string) => (
      <span className="text-sm text-khiimori-text-muted whitespace-nowrap">{value}</span>
    ),
  },
  );

  if (onDelete) {
    cols.push({
      key: 'actions',
      header: '',
      width: '3rem',
      render: (_value, row) => (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row.id);
            }}
            className="p-1.5 rounded-md text-khiimori-text-muted hover:text-error-400 hover:bg-error-500/10 transition-colors"
            title="Delete tab"
            aria-label={`Delete ${row.title}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    });
  }

  return cols;
}

interface TabSecurityTableProps {
  rows: TabSecurityRow[];
  isLoading?: boolean;
  onScanAll?: () => void;
  isScanning?: boolean;
  showFooterCards?: boolean;
  onRowClick?: (row: TabSecurityRow) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onSelectAll?: (select: boolean) => void;
  onDeleteTab?: (id: string) => void;
  searchable?: boolean;
}

export function TabSecurityTable({
  rows,
  isLoading = false,
  onScanAll,
  isScanning = false,
  showFooterCards = false,
  onRowClick,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onDeleteTab,
  searchable = true,
}: TabSecurityTableProps) {
  const selectAllRef = useRef<HTMLInputElement>(null);
  const selection =
    selectedIds && onToggleSelect
      ? { selectedIds, onToggle: onToggleSelect }
      : undefined;

  const columns = useMemo(
    () => buildSecurityColumns(selection, onDeleteTab),
    [selection, onDeleteTab],
  );

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds?.has(r.id));
  const someSelected = Boolean(selectedIds && selectedIds.size > 0 && !allSelected);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const safeTabs = useMemo(() => rows.filter((t) => t.status === 'safe').length, [rows]);
  const warningTabs = useMemo(() => rows.filter((t) => t.status === 'warning').length, [rows]);
  const totalThreats = useMemo(
    () => rows.reduce((sum, tab) => sum + tab.findings.length, 0),
    [rows],
  );
  const recentAlerts = useMemo(() => {
    const out: { key: string; text: string; high: boolean }[] = [];
    for (const row of rows) {
      for (const f of row.findings) {
        if (f.severity === 'low') continue;
        out.push({ key: `${row.id}-${f.type}`, text: `${f.description} (${row.title})`, high: f.severity === 'high' });
      }
    }
    return out.sort((a, b) => Number(b.high) - Number(a.high)).slice(0, 5);
  }, [rows]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {onScanAll && (
        <TabSecuritySummaryStrip
          totalTabs={rows.length}
          safeTabs={safeTabs}
          warningTabs={warningTabs}
          totalThreats={totalThreats}
          onScanAll={onScanAll}
          isScanning={isScanning}
        />
      )}

      <DataGrid
        data={rows}
        columns={columns}
        searchable={searchable}
        onRowClick={
          onRowClick ??
          (selection
            ? (row) => selection.onToggle(row.id)
            : undefined)
        }
        leadingHeader={
          selection && onSelectAll && rows.length > 0 ? (
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={allSelected}
              onChange={(e) => onSelectAll(e.target.checked)}
              className={CHECKBOX_CLASS}
              aria-label="Select all tabs"
            />
          ) : undefined
        }
      />

      {showFooterCards && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6">
            <h3 className="font-semibold text-khiimori-text-primary mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Security Best Practices
            </h3>
            <ul className="space-y-2 text-sm text-khiimori-text-muted">
              <li>Always use HTTPS sites</li>
              <li>Avoid suspicious domains</li>
              <li>Keep browser updated</li>
              <li>Enable tracking protection</li>
            </ul>
          </Card>
          <Card className="p-6">
            <h3 className="font-semibold text-khiimori-text-primary mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Recent Alerts
            </h3>
            {recentAlerts.length === 0 ? (
              <p className="text-sm text-khiimori-text-muted">No medium or high risk findings.</p>
            ) : (
              <div className="space-y-2 text-sm">
                {recentAlerts.map((a) => (
                  <p key={a.key} className={a.high ? 'text-red-400' : 'text-yellow-400'}>
                    {a.text}
                  </p>
                ))}
              </div>
            )}
          </Card>
          <Card className="p-6">
            <h3 className="font-semibold text-khiimori-text-primary mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500" />
              What Is Checked
            </h3>
            <p className="text-sm text-khiimori-text-muted">
              Scans inspect each tab&apos;s URL for risky patterns. Page content is not inspected, so this is not
              malware or virus scanning.
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}
